import React, { useState, useEffect } from "react";
import DModal from "@/components/Global/DModal";
import DButton from "@/components/Global/DButton";
import DSwitch from "@/components/Global/DSwitch";
import CheckmarkIcon from "@/components/Global/Icons/CheckmarkIcon";
import InfoIcon from "@/components/Global/Icons/InfoIcon";
import useDanteApi from "@/hooks/useDanteApi";
import * as plansService from "@/services/plans.service";
import { useUserStore } from "@/stores/user/userStore";
import DModalDowngrade from "../DModalDowngrade";
import DLoading from "@/components/DLoading";
import DTooltip from "@/components/Global/DTooltip";
import { Transition } from "@headlessui/react";
import clsx from "clsx";

// Define the features list by plan
const STARTER_FEATURES = [
  { label: "5 AI Chatbots" },
  { label: "12,000 messages/month", hasTooltip: true },
  { label: "100+ Languages" },
  { label: "Basic LLMs" },
  { label: "Email Support" },
  { label: "Custom Styling" },
  { label: "Custom Personalities" },
  { label: "AI Chatbot Deployment" },
  { label: "Chat Records" },
  { label: "Analytics" },
  { label: "AI Lead Generation" },
  { label: "WordPress Integration" },
];

const ADVANCED_FEATURES = [
  { label: "10 AI Chatbots" },
  { label: "50,000 messages/month", hasTooltip: true },
  { label: "Everything in Starter, plus:" },
  { label: "Advanced LLMs" },
  { label: "Priority Support" },
  { label: "Remove Dante AI Branding" },
  { label: "Human Handover" },
  { label: "6,000+ Integrations" },
  { label: "Dante AI API Access" },
  { label: "1 Additional Team Member" },
  { label: "6,000+ Additional Integrations" },
  { label: "Conversation Rate Limiting" },
];

const PRO_FEATURES = [
  { label: "50 AI Chatbots" },
  { label: "200,000 messages/month", hasTooltip: true },
  { label: "Everything in Advanced, plus:" },
  { label: "Dedicated Account Manager" },
  { label: "Intercom Integration" },
  { label: "Personalised Onboarding" },
  { label: "Uptime SLA" },
  { label: "Use own LLM API Key" },
  { label: "4 Additional Team Members" },
  { label: "API Setup" },
  { label: "Video Call Support" },
  { label: "Prompt Engineering Assistance" },
];

// Tooltip content for messages feature
const MESSAGES_TOOLTIP =
  "This is the maximum number of messages your AI chatbot will send per month, depending on which LLM you use. For a full breakdown of LLM message costs, visit the FAQ here.";

// Plan descriptions
const PLAN_DESCRIPTIONS = {
  Starter: "For small businesses",
  Advanced: "For businesses wanting more robust AI features",
  Pro: "For businesses wanting more advanced AI",
};

const DModalPlans = ({ isOpen, onClose }) => {
  const user = useUserStore((state) => state.user);
  const [annualDiscount, setAnnualDiscount] = useState(false);
  const [nextTier, setNextTier] = useState(null);
  const [isDowngradeModalOpen, setIsDowngradeModalOpen] = useState(false);
  const [isContentReady, setIsContentReady] = useState(false);
  const { data: tiers, isLoading } = useDanteApi(plansService.getTiers);

  // Control when to show content after data is loaded
  useEffect(() => {
    if (!isLoading && tiers) {
      // Add a small delay to ensure smooth animation
      const timer = setTimeout(() => {
        setIsContentReady(true);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setIsContentReady(false);
    }
  }, [isLoading, tiers, isOpen]);

  const period = annualDiscount ? "yearly" : "monthly";

  const getPlanSavings = (yearlyPrice, monthlyPrice) => {
    if (period === "yearly") {
      let priceYearly = yearlyPrice;
      let priceMonthly = monthlyPrice;

      return Math.round(priceMonthly * 12 - priceYearly);
    }
    return 0;
  };

  const handleUpgrade = async (tierId) => {
    try {
      const response = await plansService.getCheckoutSession(tierId);
      if (response.status === 200) {
        window.open(response.data.checkout_session.url, "_blank");
        onClose();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleDowngrade = (tier) => {
    setNextTier(tier);
    setIsDowngradeModalOpen(true);
  };

  const handleContactSales = () => {
    window.open(
      "https://calendly.com/jetelira/enterprise-introduction",
      "_blank"
    );
    onClose();
  };

  // Assume tier_rank: 1 = Starter, 2 = Advanced, 3 = Pro
  // Filter tiers to only include Starter, Advanced, and Pro plans
  const filteredTiers =
    tiers?.[period]?.filter((tier) =>
      ["Starter", "Advanced", "Pro"].includes(tier.name)
    ) || [];

  // Determine if a tier should show the "BEST VALUE" label
  // Always show for Advanced plan regardless of monthly/yearly
  const isBestValue = (tierName) => tierName === "Advanced";

  // User's tier rank is used in the component for comparison

  // Don't open the modal until we have data
  const shouldOpenModal = Boolean(isOpen && (tiers || isLoading));

  return (
    <DModal
      isOpen={shouldOpenModal}
      onClose={onClose}
      title="Upgrade your plan"
      subtitle="Unlock customer communication at scale with Dante AI."
      className="w-[1100px] max-w-none !p-size2 !pt-size1 !rounded-2xl overflow-hidden bg-[oklch(97%_.001_286.4)] !max-h-[90vh]"
      headerClassName="!gap-size1 !justify-center text-center w-full"
      titleClassName="!text-xl text-center w-full"
      subtitleClassName="!text-xs text-center w-full"
      contentBgColor="pt-size3 bg-[oklch(97%_.001_286.4)]"
      hideCloseButton={false}
      line={false}
    >
      {/* Loading state */}
      <Transition
        show={isLoading}
        enter="transition-opacity duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="flex items-center justify-center h-[50vh] w-full">
          <DLoading show={true} />
        </div>
      </Transition>

      {/* Content */}
      <Transition
        show={!isLoading && isContentReady}
        enter="transition-all duration-500"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition-all duration-300"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <div
          className={clsx(
            "flex flex-col gap-size2 max-h-[80vh] overflow-y-auto",
            "animate-fadeIn"
          )}
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "#ccc #f1f1f1",
          }}
        >
          {/* Annual discount toggle - fixed position */}
          <div className="sticky top-0 z-10 flex items-center justify-center mb-size1 pb-size1 pt-0">
            <div className="inline-flex items-center gap-size2 px-size3 py-0.5 rounded-full shadow-sm">
              <span className="text-xs font-medium">Monthly</span>
              <DSwitch
                checked={annualDiscount}
                onChange={setAnnualDiscount}
                className="!gap-0"
              />
              <div className="flex items-center gap-size1 py-size0">
                <span className="text-xs font-medium">Annual</span>
                {annualDiscount && (
                  <span className="text-[10px] bg-green-50 text-green-700 px-1.5 py-0.5 rounded-full font-medium">
                    2 months free
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Plans grid */}
          <div className="grid md:grid-cols-3 gap-size2">
            {filteredTiers.map((tier, index) => {
              const isAdvanced = tier.name === "Advanced";

              return (
                <div
                  key={tier.id}
                  className={`
                  bg-white rounded-xl
                  ${isAdvanced ? "py-[25px] -mt-size2" : "py-size4"}
                  px-size6
                  ${
                    isAdvanced
                      ? "border-2 border-purple-200"
                      : "border border-grey-5"
                  }
                  flex flex-col relative shadow-lg
                  ${
                    index === 0
                      ? "animate-fadeInUpDelayed1"
                      : index === 1
                      ? "animate-fadeInUpDelayed2"
                      : "animate-fadeInUpDelayed3"
                  }
                `}
                >
                  {/* Best Value Label for Advanced Plan */}
                  {isBestValue(tier.name) && (
                    <div className="absolute -top-2 right-[-3px] transform bg-gradient-to-r from-purple-600 to-purple-400 text-white px-4 py-0.5 rounded-md text-[10px] font-bold tracking-wider shadow-md">
                      BEST VALUE
                    </div>
                  )}

                  <div className="flex flex-col gap-[0.4rem]">
                    {/* Plan Header */}
                    <div className="text-center">
                      <h3 className="text-xl font-medium mb-0.5">
                        {tier.name}
                      </h3>
                      <p className="text-xs text-grey-50">
                        {PLAN_DESCRIPTIONS[tier.name]}
                      </p>
                    </div>

                    {/* Price */}
                    <div className="flex flex-col items-center">
                      <span className="text-xl font-bold">
                        $
                        {period === "yearly"
                          ? Math.round(tier.price / 12)
                          : tier.price}
                      </span>
                      <p className="text-xs text-grey-50">
                        per{" "}
                        {period === "yearly"
                          ? "month, billed annually"
                          : "month"}
                      </p>
                      {period === "yearly" && (
                        <p className="text-[10px] text-green-700">
                          Save $
                          {getPlanSavings(
                            tier.price,
                            tiers?.["monthly"]?.find(
                              (t) => t.name === tier.name
                            )?.price
                          )}{" "}
                          per year
                        </p>
                      )}
                    </div>

                    {/* Button Logic */}
                    {user?.tier_rank === tier.rank && !user?.tier_on_trial && (
                      <DButton
                        variant="light"
                        fullWidth
                        className={`relative overflow-hidden border border-purple-200 ${
                          isAdvanced ? "py-size2 px-size2" : "p-size2"
                        } text-black rounded-full !mt-auto`}
                      >
                        Current Plan
                      </DButton>
                    )}
                    {user?.tier_rank > tier.rank &&
                      tier.name !== "Enterprise" &&
                      !user?.tier_on_trial && (
                        <DButton
                          fullWidth
                          onClick={() => handleDowngrade(tier)}
                          className={`border border-purple-200 ${
                            isAdvanced ? "py-size2 px-size2" : "p-size2"
                          } text-black rounded-full`}
                        >
                          Downgrade
                        </DButton>
                      )}
                    {(user?.tier_rank || 0) < tier.rank &&
                      tier.name !== "Enterprise" &&
                      !user?.tier_on_trial && (
                        <DButton
                          fullWidth
                          onClick={() => handleUpgrade(tier.id)}
                          className={`border border-purple-200 ${
                            isAdvanced
                              ? "py-size2 px-size2 text-white"
                              : "p-size2 text-black"
                          }  rounded-full`}
                          style={{
                            background: isAdvanced
                              ? "linear-gradient(87deg, rgb(100, 116, 255) -15.34%, rgb(128, 120, 253) 21.77%, rgb(207, 192, 254) 83.94%) 0% 0% / 123.26% 100% border-box"
                              : undefined,
                          }}
                        >
                          Go {tier.name}
                        </DButton>
                      )}
                    {tier?.name !== "Enterprise" && user?.tier_on_trial && (
                      <DButton
                        fullWidth
                        onClick={() => handleUpgrade(tier.id)}
                        className={`border border-purple-200 ${
                          isAdvanced
                            ? "py-size2 px-size2 text-white"
                            : "p-size2 text-black"
                        }  rounded-full`}
                        style={{
                          background: isAdvanced
                            ? "linear-gradient(87deg, rgb(100, 116, 255) -15.34%, rgb(128, 120, 253) 21.77%, rgb(207, 192, 254) 83.94%) 0% 0% / 123.26% 100% border-box"
                            : undefined,
                        }}
                      >
                        Go {tier.name}{" "}
                        {user?.tier_rank === tier.rank && user?.tier_on_trial}
                      </DButton>
                    )}

                    {/* Features Section */}
                    <div className="flex flex-col gap-size0 mx-auto">
                      <ul className="flex flex-col gap-[0.15rem]">
                        {(tier.name === "Starter"
                          ? STARTER_FEATURES
                          : tier.name === "Advanced"
                          ? ADVANCED_FEATURES
                          : PRO_FEATURES
                        ).map((feature, featureIndex) => {
                          return (
                            <li
                              key={featureIndex}
                              className="flex items-center gap-size1"
                            >
                              <div className="w-3 flex items-center justify-center">
                                {feature.label.includes("Everything in") ? (
                                  <span className="text-xs text-grey-50"></span>
                                ) : (
                                  <CheckmarkIcon
                                    className={`text-purple-400 w-3 h-3`}
                                  />
                                )}
                              </div>
                              <div className="flex items-center gap-size1">
                                <span
                                  className={`text-xs ${
                                    feature.label.includes("Everything in")
                                      ? "font-bold my-size0"
                                      : ""
                                  }`}
                                >
                                  {feature.label}
                                </span>
                                {feature.hasTooltip && (
                                  <DTooltip
                                    content={
                                      <div className="max-w-[250px]">
                                        <p>
                                          {MESSAGES_TOOLTIP}{" "}
                                          <a
                                            href="https://www.dante-ai.com/pricing"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-white hover:underline"
                                          >
                                            Learn more
                                          </a>{" "}
                                        </p>
                                      </div>
                                    }
                                  >
                                    <InfoIcon className="w-3 h-3 text-grey-50 cursor-help" />
                                  </DTooltip>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex justify-center">
            <a
              href="https://www.dante-ai.com/pricing"
              className="text-purple-300 text-xs flex items-center gap-size1"
              target="_blank"
              // onClick={(e) => {
              //   e.preventDefault();
              //   onClose();
              //   window.location.href = '/plans';
              // }}
            >
              See all plan features <span className="text-sm">→</span>
            </a>
          </div>

          {/* Enterprise section */}
          <div className="rounded-xl p-3 border border-grey-5 flex flex-wrap items-center justify-between animate-fadeInUpDelayed3">
            <div className="flex flex-col">
              <h3 className="text-base font-medium">Enterprise</h3>
              <p className="text-xs text-grey-50">
                Custom solutions for large teams with advanced needs. Custom
                pricing based on your requirements.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <DButton
                className="border border-purple-200 p-size2 px-size3 text-black rounded-full"
                size="xs"
                onClick={handleContactSales}
              >
                Contact Us
              </DButton>
            </div>
          </div>
        </div>
      </Transition>
      <DModalDowngrade
        isOpen={isDowngradeModalOpen}
        onClose={() => setIsDowngradeModalOpen(false)}
        nextTier={nextTier}
      />
    </DModal>
  );
};

export default DModalPlans;
